import { useEffect, useRef, useCallback } from 'react';
import { configurationService, UIConfiguration } from '../services/configurationService';

interface UseAutoRefreshOptions {
  enabled?: boolean;
  onRefresh: () => Promise<void> | void;
  shouldRefresh?: () => boolean | Promise<boolean>; // Optional condition function
}

export function useAutoRefresh({ enabled = true, onRefresh, shouldRefresh }: UseAutoRefreshOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const configRef = useRef<UIConfiguration | null>(null);
  const refreshCallbackRef = useRef(onRefresh);

  // Update callback ref when onRefresh changes
  refreshCallbackRef.current = onRefresh;

  const startAutoRefresh = useCallback(async () => {
    if (!enabled) return;

    try {
      // Get UI configuration
      const config = await configurationService.getUIConfiguration();
      configRef.current = config;

      // Only start if auto-refresh is enabled in config
      if (!config.enableAutoRefresh) return;

      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Create refresh function that respects visibility setting and custom condition
      const refreshFunction = async () => {
        try {
          // Check if we should only refresh when visible
          if (config.refreshOnlyWhenVisible && document.hidden) {
            return;
          }

          // Check custom condition if provided
          if (shouldRefresh) {
            const shouldProceed = await shouldRefresh();
            if (!shouldProceed) {
              return;
            }
          }
          
          await refreshCallbackRef.current();
        } catch (error) {
          console.error('Auto-refresh failed:', error);
        }
      };

      // Set up the interval
      intervalRef.current = setInterval(
        refreshFunction,
        config.autoRefreshIntervalSeconds * 1000
      );

      console.log(`Auto-refresh started: ${config.autoRefreshIntervalSeconds}s interval, visible-only: ${config.refreshOnlyWhenVisible}, condition-based: ${shouldRefresh !== undefined}`);
    } catch (error) {
      console.error('Failed to start auto-refresh:', error);
    }
  }, [enabled]);

  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log('Auto-refresh stopped');
    }
  }, []);

  // Handle visibility change for refreshOnlyWhenVisible option
  useEffect(() => {
    if (!configRef.current?.refreshOnlyWhenVisible) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && configRef.current?.enableAutoRefresh) {
        // Page became visible, trigger immediate refresh
        refreshCallbackRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Start auto-refresh when component mounts or enabled changes
  useEffect(() => {
    if (enabled) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }

    // Cleanup on unmount
    return stopAutoRefresh;
  }, [enabled, startAutoRefresh, stopAutoRefresh]);

  return {
    startAutoRefresh,
    stopAutoRefresh,
    config: configRef.current
  };
}